/**
 * Shared "you are browsing in X" country display.
 *
 * Used by the footer's location selector and the header menu's country row so
 * both name the current market identically — they used to be one inline block
 * in the footer, which meant a second copy the moment the header needed it too.
 */

import type { LocaleOptions } from "@/api/locale";
import { getCountryFlagSrc } from "@/utils/locale-url";

export interface LocaleDisplay {
  /** Country name in the current language, or `fallbackLabel` when unknown. */
  label: string;
  /** Round flag asset for the country, or null when there is no flag for it. */
  flagSrc: string | null;
}

interface ResolveLocaleDisplayOptions {
  /** Full culture code, e.g. "fi-FI" — only its language half is used. */
  currentLocale: string;
  currentCountry?: string;
  localeOptions?: LocaleOptions;
  /** Shown while the locale payload is still loading, e.g. "Global". */
  fallbackLabel: string;
}

/**
 * Resolves the country name in the current language, falling back to the
 * country's first display name and then to `fallbackLabel`. The flag is only
 * returned for a country the API actually knows about, so an unrecognised
 * country renders as a globe rather than a broken image.
 */
export function resolveLocaleDisplay({
  currentLocale,
  currentCountry,
  localeOptions,
  fallbackLabel,
}: ResolveLocaleDisplayOptions): LocaleDisplay {
  if (!currentCountry || !localeOptions) {
    return { label: fallbackLabel, flagSrc: null };
  }

  const country = localeOptions.countries.find(
    (c) => c.countryCode === currentCountry
  );
  if (!country) {
    return { label: fallbackLabel, flagSrc: null };
  }

  const languageCode = currentLocale.split("-")[0];
  const displayName =
    country.displayNames.find((d) => d.languageCode === languageCode)?.name ??
    country.displayNames[0]?.name;

  return {
    label: displayName ?? fallbackLabel,
    flagSrc: getCountryFlagSrc(currentCountry),
  };
}
